/**
 * Manual requirement entry — the beta fallback for obligations that arrive
 * outside the document-intelligence pipeline.
 *
 * Fields mirror the ingest schema exactly. An operator-created row without a
 * citation is recorded as OPERATOR ENTERED and stays UNVERIFIED — the server
 * downgrades any over-claimed evidence and reports the adjustment, which is
 * surfaced rather than hidden. Blank durations mean UNKNOWN, never zero.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import apiClient from "../../services/api";
import {
  requirementsApi,
  classifyWorkflowError,
} from "../../services/intelligenceService";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import { Loader2, UserPen } from "lucide-react";

/** The register's known types (VARCHAR server-side; unknown types stored + warned). */
const REQUIREMENT_TYPES = [
  "SUBMITTAL", "SHOP_DRAWING", "PRODUCT_DATA", "SAMPLE", "MATERIAL_APPROVAL",
  "GOVERNMENT_REVIEW", "OWNER_REVIEW", "DESIGN_REVIEW", "PERMIT", "INSPECTION",
  "TEST", "COMMISSIONING", "OUTAGE_REQUEST", "UTILITY_SHUTDOWN", "LOCKOUT_TAGOUT",
  "ACCESS_AUTHORIZATION", "SECURITY_CLEARANCE", "BADGING", "ENVIRONMENTAL_APPROVAL",
  "SAFETY_PLAN", "QUALITY_CONTROL_PLAN", "PREPARATORY_MEETING", "INITIAL_INSPECTION",
  "FOLLOWUP_INSPECTION", "MOCKUP", "LONG_LEAD_MATERIAL", "FABRICATION", "DELIVERY",
  "NOTICE", "REPORT", "CLOSEOUT", "TRAINING", "WARRANTY", "OM_MANUAL", "AS_BUILT",
  "OTHER",
];

const EMPTY = {
  requirementType: "SUBMITTAL",
  title: "",
  specSection: "",
  reference: "",
  description: "",
  requiredDate: "",
  reviewDays: "",
  preparationDays: "",
  requiredReviewer: "",
  activityId: "",
  bidPackageId: "",
  procurementItemId: "",
  sourceDocumentId: "",
  sourceQuote: "",
  sourcePage: "",
  notes: "",
};

const AddRequirementDialog = ({ open, onOpenChange, projectId, activities = [], onCreated }) => {
  const [form, setForm] = useState(EMPTY);
  const [packages, setPackages] = useState([]);
  const [procItems, setProcItems] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY);
    apiClient
      .get("/bids", { params: { project_id: projectId } })
      .then(({ data }) => setPackages((data || []).filter((b) => b.project_id === projectId)))
      .catch(() => setPackages([]));
    apiClient
      .get("/procurement", { params: { project_id: projectId } })
      .then(({ data }) => setProcItems(data || []))
      .catch(() => setProcItems([]));
  }, [open, projectId]);

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));

  const hasCitation = Boolean(form.sourceDocumentId.trim() && form.sourceQuote.trim());

  const save = async () => {
    if (!form.title.trim()) {
      toast.error("A requirement needs a title.");
      return;
    }
    // The register refuses to invent identity from a type alone — it needs a
    // spec section, a reference, or a source document to key the obligation.
    if (!form.specSection.trim() && !form.reference.trim() && !form.sourceDocumentId.trim()) {
      toast.error(
        "A requirement needs a spec section, a reference, or a source document to identify it."
      );
      return;
    }
    const row = {
      requirementType: form.requirementType,
      title: form.title.trim(),
    };
    if (form.specSection.trim()) row.specSection = form.specSection.trim();
    if (form.reference.trim()) row.reference = form.reference.trim();
    if (form.description.trim()) row.description = form.description.trim();
    if (form.requiredDate) row.requiredDate = form.requiredDate;
    if (form.reviewDays !== "") row.reviewDays = Number(form.reviewDays);
    if (form.preparationDays !== "") row.preparationDays = Number(form.preparationDays);
    if (form.requiredReviewer.trim()) row.requiredReviewer = form.requiredReviewer.trim();
    if (form.activityId) row.activityId = form.activityId;
    if (form.bidPackageId) row.bidPackageId = form.bidPackageId;
    if (form.procurementItemId) row.procurementItemId = form.procurementItemId;
    if (form.sourceDocumentId.trim()) row.sourceDocumentId = form.sourceDocumentId.trim();
    if (form.sourceQuote.trim()) row.sourceQuote = form.sourceQuote.trim();
    if (form.sourcePage !== "") row.sourcePage = Number(form.sourcePage);
    // Truthful provenance: the row states it was operator-entered.
    const noteParts = ["Operator entered via OMNΛS UI."];
    if (form.notes.trim()) noteParts.push(form.notes.trim());
    row.notes = noteParts.join(" ");

    setSaving(true);
    try {
      const result = await requirementsApi.ingest(projectId, [row]);
      const made = result.created ?? 0;
      const updated = result.updated ?? 0;
      toast.success(
        made > 0
          ? "Requirement recorded."
          : updated > 0
            ? "An existing requirement with the same identity was updated instead of duplicated."
            : "Recorded."
      );
      (result.adjustments || []).forEach((a) =>
        toast.info(typeof a === "string" ? a : a.message || JSON.stringify(a))
      );
      (result.warnings || []).forEach((w) =>
        toast.warning(typeof w === "string" ? w : w.message || JSON.stringify(w))
      );
      onOpenChange(false);
      onCreated?.();
    } catch (err) {
      toast.error(classifyWorkflowError(err).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add requirement</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-lg bg-muted/30 px-3 py-2 flex items-start gap-2">
            <UserPen className="w-4 h-4 mt-0.5 text-amber-400 shrink-0" />
            <p className="text-xs text-muted-foreground">
              This will be recorded as{" "}
              <Badge variant="outline" className="bg-amber-500/20 text-amber-400 border-amber-500/30 font-normal mx-0.5">
                OPERATOR ENTERED
              </Badge>
              {hasCitation ? (
                <>with your document citation as evidence — the register still verifies the
                document before treating it as cited.</>
              ) : (
                <>and{" "}
                <Badge variant="outline" className="bg-muted text-muted-foreground border-border font-normal mx-0.5">
                  UNVERIFIED
                </Badge>
                — not contractual evidence — until it is cited to a source document or
                verified by a named operator.</>
              )}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Type *</Label>
              <Select value={form.requirementType} onValueChange={set("requirementType")}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-64">
                  {REQUIREMENT_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t.replaceAll("_", " ")}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Required date</Label>
              <Input type="date" className="mt-1" value={form.requiredDate} onChange={(e) => set("requiredDate")(e.target.value)} />
            </div>
          </div>
          <div>
            <Label className="text-xs">Title *</Label>
            <Input className="mt-1" value={form.title} onChange={(e) => set("title")(e.target.value)} placeholder="e.g. Switchgear product data submittal" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Spec section</Label>
              <Input className="mt-1" value={form.specSection} onChange={(e) => set("specSection")(e.target.value)} placeholder="e.g. 26 24 13" />
            </div>
            <div>
              <Label className="text-xs">Reference</Label>
              <Input className="mt-1" value={form.reference} onChange={(e) => set("reference")(e.target.value)} placeholder="e.g. RFI-014, permit no." />
            </div>
          </div>
          <p className="text-[10px] text-muted-foreground -mt-1">
            At least one of spec section, reference, or a source document is required — a
            type alone cannot identify an obligation.
          </p>
          <div>
            <Label className="text-xs">Description</Label>
            <Textarea className="mt-1" rows={2} value={form.description} onChange={(e) => set("description")(e.target.value)} />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Prep (days)</Label>
              <Input type="number" min="0" className="mt-1" value={form.preparationDays} onChange={(e) => set("preparationDays")(e.target.value)} placeholder="unknown" />
            </div>
            <div>
              <Label className="text-xs">Review (days)</Label>
              <Input type="number" min="0" className="mt-1" value={form.reviewDays} onChange={(e) => set("reviewDays")(e.target.value)} placeholder="unknown" />
            </div>
            <div>
              <Label className="text-xs">Reviewer</Label>
              <Input className="mt-1" value={form.requiredReviewer} onChange={(e) => set("requiredReviewer")(e.target.value)} placeholder="e.g. Government" />
            </div>
          </div>
          <p className="text-[10px] text-muted-foreground -mt-1">
            Blank durations are recorded as UNKNOWN — the scheduler reports the gap instead
            of assuming a default.
          </p>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Activity</Label>
              <Select value={form.activityId || "none"} onValueChange={(v) => set("activityId")(v === "none" ? "" : v)}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-56">
                  <SelectItem value="none">None</SelectItem>
                  {activities.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Bid package</Label>
              <Select value={form.bidPackageId || "none"} onValueChange={(v) => set("bidPackageId")(v === "none" ? "" : v)}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-56">
                  <SelectItem value="none">None</SelectItem>
                  {packages.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Procurement item</Label>
              <Select value={form.procurementItemId || "none"} onValueChange={(v) => set("procurementItemId")(v === "none" ? "" : v)}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-56">
                  <SelectItem value="none">None</SelectItem>
                  {procItems.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2 rounded-lg border border-border p-3">
            <p className="text-xs font-medium text-muted-foreground">
              Evidence (optional — makes this a cited requirement)
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Source document id</Label>
                <Input className="mt-1" value={form.sourceDocumentId} onChange={(e) => set("sourceDocumentId")(e.target.value)} placeholder="Document on this project" />
              </div>
              <div>
                <Label className="text-xs">Page</Label>
                <Input type="number" min="1" className="mt-1" value={form.sourcePage} onChange={(e) => set("sourcePage")(e.target.value)} />
              </div>
            </div>
            <div>
              <Label className="text-xs">Quoted text</Label>
              <Textarea className="mt-1" rows={2} value={form.sourceQuote} onChange={(e) => set("sourceQuote")(e.target.value)} placeholder="Exact language from the document" />
            </div>
          </div>

          <div>
            <Label className="text-xs">Notes</Label>
            <Textarea className="mt-1" rows={2} value={form.notes} onChange={(e) => set("notes")(e.target.value)} />
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button size="sm" onClick={save} disabled={saving}>
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />}
              Record requirement
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddRequirementDialog;
